/**
 * 通知展示判定与内容成形（任务 18b）。
 *
 * 纯模型：不碰 window / Notification / 请求，全部输入经 `NotifyContext` 注入，
 * 可在 node 环境直接单测（同 18a 的 pushCapability.ts）。
 *
 * **事实源是会话行**：新消息到达时 Worker 已把会话行（unread / lastMsg / isAt /
 * noDisturbing）推到主线程（`chat/conversations` upsert），通知内容全部取自那一行
 * 与本地设置——**不允许为了弹通知再发任何 HTTP**（PROMPT 最少请求原则；
 * docs/push.md §6）。这也顺带解决了「群消息要不要提醒」：isAt 是服务端算好的
 * @我 信号，客户端不自己解析 @。
 *
 * 过滤顺序是刻意的（从「根本不该提醒」到「此刻不必提醒」），`NotifySkipReason`
 * 一一对应，调试时能直接看出被哪一条拦下：
 *  1. `not-granted`       没有通知权限（连 showNotification 都不能调）
 *  2. `disabled`          总开关关（notifyModel.enabled，关=全静默）
 *  3. `no-new-message`    lastMsgId 没前进（对账/已读回执/置顶等 upsert 同样会回流）
 *  4. `own-message`       自己在别端发的消息
 *  5. `read-elsewhere`    unread=0：别端已读，或 bump 的是不计未读的提示条类型
 *  6. `muted`             会话免打扰（与角标同口径：既不弹也不计数，18a badgeCountOf）
 *  7. `mention-only`      仅提醒 @我和私聊 → 群内非 @我 的消息不提醒（私聊不受此开关影响）
 *  8. `active-conversation` 前台且正开着这个会话（用户正看着）
 *  9. `foreground`        前台且开着别的会话（列表角标已经在提示；此档交给**窗口内
 *                         提示条**那条通道，见下面的 shouldPopupInApp，18c2b）
 *
 * 「前台」= 可见**且**窗口有焦点。只判 visibilityState 不够：桌面把浏览器窗口压在
 * 别的应用后面时页面仍是 visible，那种情况必须弹系统通知。
 *
 * 两条通道（系统通知 / 窗口内提示条，18c2b）共用 2~7 档过滤（`commonSkipReason`），
 * 前台判定上互补：系统通知只在**非前台**弹，提示条只在**前台**弹，永不重复。
 */
import { resolveMediaUrl } from '~/chat/mediaUrl';
import { routePath } from '~/nav/routes';
import type { NotifySettings } from '~/shell/notifyModel';
import type { ConversationRecord } from '~/worker/contract';

/** 通知折叠标签前缀：同一会话的通知互相替换，不堆成一屏 */
export const NOTIFY_TAG_PREFIX = 'lhc-chat:';

export function notifyTagOf(conversationKey: string): string {
  return NOTIFY_TAG_PREFIX + conversationKey;
}

/** 点击通知要去的站内路径（路由口径单点在 nav/routes，SW 侧也用它） */
export function notifyTargetUrl(conversationKey: string): string {
  return routePath({ kind: 'chat', conversationKey });
}

/** 没有预览内容时的兜底正文（showPreview 关 / 预览为空串） */
const BODY_FALLBACK = '发来一条新消息';
const BODY_ENCRYPTED = '[加密消息]';
const MENTION_PREFIX = '[有人@我] ';
/** 名称缺失（占位行等补拉）时的标题兜底，不造假名 */
const TITLE_FALLBACK = '新消息';

export interface NotifyContext {
  readonly settings: NotifySettings;
  /** 当前打开的会话键（桌面双栏右栏开着也算打开）；无则 null */
  readonly activeKey: string | null;
  /** `document.visibilityState === 'visible'` */
  readonly pageVisible: boolean;
  /** `document.hasFocus()`：可见但窗口在别的应用后面时为 false */
  readonly windowFocused: boolean;
  /** 我的 uid（0=未知，则不按发送者排除，交由 unread 兜底） */
  readonly myUid: number;
  /** 通知权限已授权（`pushSupportOf(env) === 'ready'`，18a） */
  readonly granted: boolean;
  /** 头像 CDN 前缀（config 未回包时空串 → 通知不带图标） */
  readonly cdnBase: string;
  /**
   * PC 布局（`!layout.isMobile`）。只影响窗口内提示条这条通道：
   * pcPopup 本来就是旧版「PC 端提醒」组的开关，设置页在移动端也不显示那一组。
   */
  readonly isDesktop: boolean;
}

export type NotifySkipReason =
  | 'not-granted'
  | 'disabled'
  | 'no-new-message'
  | 'own-message'
  | 'read-elsewhere'
  | 'muted'
  | 'mention-only'
  | 'active-conversation'
  | 'foreground';

export interface NotifyContent {
  readonly title: string;
  readonly body: string;
  /** 折叠标签（同会话替换） */
  readonly tag: string;
  /** 空串 = 不带图标（CDN 未就绪 / 会话无头像） */
  readonly icon: string;
  readonly conversationKey: string;
  /** 点击后要落的站内路径 */
  readonly url: string;
  /** 该会话未读数（角标/多条合并文案用） */
  readonly unread: number;
  readonly msgId: number;
  /**
   * 静音（`!settings.sound`，18c2b）：系统通知透传给 `NotificationOptions.silent`，
   * 窗口内提示条据此决定要不要放提示音。成形在这里是为了「内容口径单点」——
   * 展示层只照着这份结果调 API，不自己读设置。
   */
  readonly silent: boolean;
}

export type NotifyDecision =
  | { readonly notify: false; readonly reason: NotifySkipReason }
  | { readonly notify: true; readonly content: NotifyContent };

/** 一次会话行回流：新行 + 本端上次见过的 lastMsgId（0=首次见） */
export interface ConversationArrival {
  readonly row: ConversationRecord;
  readonly seenLastMsgId: number;
}

/** 两条通道共用的 2~7 档过滤：返回第一条拦下它的理由，null=放行 */
export type CommonSkipReason = Extract<
  NotifySkipReason,
  'disabled' | 'no-new-message' | 'own-message' | 'read-elsewhere' | 'muted' | 'mention-only'
>;

function commonSkipReason(
  arrival: ConversationArrival,
  ctx: NotifyContext,
): CommonSkipReason | null {
  const { row, seenLastMsgId } = arrival;
  const { remote } = row;
  if (!ctx.settings.enabled) return 'disabled';
  if (remote.lastMsgId <= seenLastMsgId) return 'no-new-message';
  if (ctx.myUid > 0 && remote.lastSendUid === ctx.myUid) return 'own-message';
  if (remote.unread <= 0) return 'read-elsewhere';
  if (remote.noDisturbing !== 0) return 'muted';
  if (ctx.settings.mentionOnly && row.key.startsWith('group:') && remote.isAt === 0) {
    return 'mention-only';
  }
  return null;
}

export function shouldNotify(arrival: ConversationArrival, ctx: NotifyContext): NotifyDecision {
  const { row } = arrival;
  const skip = (reason: NotifySkipReason): NotifyDecision => ({ notify: false, reason });

  if (!ctx.granted) return skip('not-granted');
  const common = commonSkipReason(arrival, ctx);
  if (common) return skip(common);
  if (ctx.pageVisible && ctx.windowFocused) {
    return skip(row.key === ctx.activeKey ? 'active-conversation' : 'foreground');
  }
  return { notify: true, content: notifyContentOf(row, ctx) };
}

// ── 窗口内提示条（18c2b，桌面 pcPopup 开关） ────────────────────────────────

/**
 * 与系统通知互补的那条通道的理由集。两处刻意差异：
 * - **不含 `not-granted`**：窗口内提示条是纯 DOM，不需要通知权限（旧版 in_app_popup
 *   同样与系统权限无关）。用户拒了系统通知，照样该在窗口里看到新消息提示。
 * - 多出 `popup-off`（开关关）/ `not-desktop`（移动端不出）/ `not-foreground`
 *   （非前台由系统通知负责，两条通道永不同时弹）。
 */
export type PopupSkipReason =
  | CommonSkipReason
  | 'popup-off'
  | 'not-desktop'
  | 'not-foreground'
  | 'active-conversation';

export type PopupDecision =
  | { readonly notify: false; readonly reason: PopupSkipReason }
  | { readonly notify: true; readonly content: NotifyContent };

export function shouldPopupInApp(
  arrival: ConversationArrival,
  ctx: NotifyContext,
): PopupDecision {
  const { row } = arrival;
  const skip = (reason: PopupSkipReason): PopupDecision => ({ notify: false, reason });

  if (!ctx.settings.pcPopup) return skip('popup-off');
  if (!ctx.isDesktop) return skip('not-desktop');
  const common = commonSkipReason(arrival, ctx);
  if (common) return skip(common);
  // 非前台 = 系统通知那条通道的辖区；此时窗口里的提示条用户根本看不见
  if (!ctx.pageVisible || !ctx.windowFocused) return skip('not-foreground');
  if (row.key === ctx.activeKey) return skip('active-conversation');
  return { notify: true, content: notifyContentOf(row, ctx) };
}

/**
 * 通知内容成形（与会话行预览同口径，conversationRowModel 的规则照搬）：
 * - 标题 = friendNote（私聊备注）优先 → nickname → 兜底「新消息」
 * - 正文：showPreview 关 → 不泄露任何内容（只说几条）；开 → 服务端算好的
 *   last_msg 预览，加密会话恒显「[加密消息]」（服务端 last_msg 是密文截断，
 *   本地 bump 的可能是明文，两路都不外显），@我 加前缀
 * - 图标 = 会话头像（已在列表里展示过 → 走浏览器 HTTP 缓存，不算新请求）
 */
export function notifyContentOf(row: ConversationRecord, ctx: NotifyContext): NotifyContent {
  const { remote } = row;
  return {
    title: remote.friendNote || remote.nickname || TITLE_FALLBACK,
    body: notifyBodyOf(row, ctx.settings.showPreview),
    tag: notifyTagOf(row.key),
    icon: resolveMediaUrl(ctx.cdnBase, remote.avatar),
    conversationKey: row.key,
    url: notifyTargetUrl(row.key),
    unread: remote.unread,
    msgId: remote.lastMsgId,
    silent: !ctx.settings.sound,
  };
}

function notifyBodyOf(row: ConversationRecord, showPreview: boolean): string {
  const { remote } = row;
  if (!showPreview) {
    return remote.unread > 1 ? `发来 ${remote.unread} 条新消息` : BODY_FALLBACK;
  }
  const text = remote.isEncrypted !== 0 ? BODY_ENCRYPTED : remote.lastMsg || BODY_FALLBACK;
  return remote.isAt !== 0 ? MENTION_PREFIX + text : text;
}
